/**
 * Utility functions for validating email addresses and preventing signup
 * with temporary / disposable email domains.
 */

export const DISPOSABLE_EMAIL_DOMAINS: Set<string> = new Set([
  "afterdo.com",
  "mailinator.com",
  "mailinator2.com",
  "mailinator.net",
  "tempmail.com",
  "tempmail.net",
  "tempmail.org",
  "temp-mail.org",
  "temp-mail.ru",
  "tempmail.us",
  "tempmail.dev",
  "tempmail.de",
  "tempmail.services",
  "temp-mail.io",
  "tempmail.live",
  "guerrillamail.com",
  "guerrillamail.info",
  "guerrillamail.biz",
  "guerrillamail.de",
  "guerrillamail.net",
  "guerrillamail.org",
  "guerrillamailblock.com",
  "sharklasers.com",
  "grr.la",
  "pokemail.net",
  "spam4.me",
  "10minutemail.com",
  "10minutemail.net",
  "10minutemail.org",
  "10minutemail.co.uk",
  "10minut.com.pl",
  "10minutemail.be",
  "20minutemail.com",
  "33mail.com",
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
  "cool.fr.nf",
  "jetable.fr.nf",
  "nospam.ze.tc",
  "nomail.xl.cx",
  "mega.poke.1af2.de",
  "speed.1af2.de",
  "courriel.fr.nf",
  "moncourrier.fr.nf",
  "monemail.fr.nf",
  "monmail.fr.nf",
  "trashmail.com",
  "trashmail.me",
  "trashmail.net",
  "trashmail.org",
  "trashmail.at",
  "trashmail.io",
  "getnada.com",
  "nada.ltd",
  "nada.kred",
  "throwawaymail.com",
  "dispostable.com",
  "dropmail.me",
  "emailondedeck.com",
  "mohmal.com",
  "mohmal.in",
  "mohmal.direct",
  "tempail.com",
  "getairmail.com",
  "maildrop.cc",
  "dismail.de",
  "fakeinbox.com",
  "inboxalias.com",
  "crazymailing.com",
  "tmail.ws",
  "burnermail.io",
  "mytemp.email",
  "minuteinbox.com",
  "generator.email",
  "discard.email",
  "discardmail.com",
  "discardmail.de",
  "receive-smss.com",
  "spoofbox.com",
  "inbrowser.org",
  "mailnesia.com",
  "mailcatch.com",
  "quickemail.info",
  "anonymousmail.com",
  "boun.cr",
  "byom.de",
  "chacuo.net",
  "0815.ru",
  "dayrep.com",
  "einrot.com",
  "fleckens.hu",
  "gustr.com",
  "jourrapide.com",
  "rhyta.com",
  "superrito.com",
  "teleworm.us",
  "armyspy.com",
  "cuvox.de",
  "lhslv.com",
  "tinynow.com",
  "tempmail.email",
  "disposablemail.com",
  "tempmailaddress.com",
  "tmpmail.org",
  "tmpmail.net",
  "disposable.com",
  "tempinbox.com",
  "fakemailgenerator.com"
]);

const DISPOSABLE_PATTERNS = [
  /^temp/i,
  /^disposable/i,
  /^trashmail/i,
  /^mailinator/i,
  /^guerrillamail/i,
  /^10minute/i,
  /^20minute/i,
  /^throwaway/i,
  /^burner/i,
  /^generator\.email$/i,
  /^fakemail/i,
  /^dropmail/i
];

export function extractDomain(email: string): string {
  if (!email || !email.includes("@")) return "";
  return email.split("@").pop()?.trim().toLowerCase() || "";
}

export function isDisposableEmail(email: string): boolean {
  if (!email) return false;
  const domain = extractDomain(email);
  if (!domain) return false;

  // 1. Direct domain match
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
    return true;
  }

  // 2. Subdomain check (e.g. sub.mailinator.com)
  const parts = domain.split(".");
  if (parts.length > 2) {
    const parentDomain = parts.slice(-2).join(".");
    if (DISPOSABLE_EMAIL_DOMAINS.has(parentDomain)) {
      return true;
    }
  }

  // 3. Pattern match
  const firstPart = parts[0];
  return DISPOSABLE_PATTERNS.some((pattern) => pattern.test(firstPart));
}
